import React, { useEffect, useState } from "react";
import {
  AntdLayout,
  AntdHeader,
  AntdContent,
  AntdFooter,
} from "../components/Base/BaseLayout";
import { Header } from "../components/Header";
import { BottomNavigation } from "../components/BottomNavigation";
import { DownloadAppBar } from "../components/DownloadAppBar";
import { PullToRefresh } from "../components/Base/PullToRefresh";
import { LayoutProps } from "./Layout.types";
import { useNavigation, ROUTES } from "../utils/navigation";
import "./Layout.scss";
import { useGlobalValue } from "../stores";
import utils from "../utils/utils";
import { isDownloadAppBarSessionClosed } from "../utils/downloadAppBarSession";
import {
  useBottomNavInviteTarget,
  useEnableDownloadAppFloatingOnHomeTop,
} from "../modules/homeDisplay/hooks";

export const Layout: React.FC<LayoutProps> = ({
  children,
  className = "",
  variant = "primary",
  testId,
  cannotRefresh,
}) => {
  const { navigate, getCurrentPath } = useNavigation();
  const [downloadAppBarVisible] = useGlobalValue("downloadAppBarVisible", true);
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const inviteTarget = useBottomNavInviteTarget();
  // 实际渲染状态：仅在 DownloadAppBar 真正显示时才推动内容下移，避免初始默认 true 时的闪动
  const [downloadAppBarActualVisible, setDownloadAppBarActualVisible] =
    useState(() => {
      if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
        return false;
      return !isDownloadAppBarSessionClosed();
    });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarActualVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  // 配置或环境变化时，主动重置一次（避免 enableFloatingOnHomeTop 由 null 变更后状态滞后）
  useEffect(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop) {
      setDownloadAppBarActualVisible(false);
    }
  }, [enableFloatingOnHomeTop]);
  const handleBottomNavClick = (itemId: string) => {
    const navigationState = { fromBottomNav: true };

    switch (itemId) {
      case "home":
        navigate(ROUTES.HOME, { state: navigationState });
        break;
      case "vip":
        navigate(ROUTES.VIP, { state: navigationState });
        break;
      case "event":
        navigate(ROUTES.EVENT_CENTER, { state: navigationState });
        break;
      case "profile":
        navigate(ROUTES.PROFILE, { state: navigationState });
        break;
      case "agent-center":
        if (inviteTarget !== "INVITE_PAGE") {
          navigate(ROUTES.AGENT_CENTER, { state: navigationState });
        } else {
          navigate(ROUTES.INVITE_FRIENDS, { state: navigationState });
        }
        break;
      default:
        console.log("Unknown navigation item:", itemId);
    }
  };

  const getActiveItem = () => {
    const currentPath = getCurrentPath();
    if (currentPath === ROUTES.EVENT_CENTER) return "event";
    if (currentPath === ROUTES.VIP) return "vip";
    if (currentPath === ROUTES.HOME || currentPath === ROUTES.HOME_ALT)
      return "home";
    if (
      currentPath === ROUTES.INVITE_FRIENDS ||
      currentPath === ROUTES.AGENT_CENTER
    )
      return "agent-center";
    if (currentPath === ROUTES.PROFILE) return "profile";
    return "home";
  };

  const shouldShowHeader = () => {
    const currentPath = getCurrentPath();
    return currentPath === ROUTES.HOME || currentPath === ROUTES.HOME_ALT;
  };

  const handleRefresh = async () => {
    window.location.reload();
  };

  return (
    <AntdLayout
      id="gbRoot"
      className={`mobile-layout layout-optimized ${className}`}
      variant={variant}
      testId={testId}
    >
      {shouldShowHeader() && (
        <AntdHeader className="mobile-layout__header" variant={variant}>
          {downloadAppBarVisible && <DownloadAppBar />}
          <Header />
        </AntdHeader>
      )}

      <AntdContent
        className="mobile-layout__content"
        style={
          shouldShowHeader() && downloadAppBarActualVisible
            ? { marginTop: "3rem" }
            : undefined
        }
      >
        <PullToRefresh onRefresh={handleRefresh} canRefresh={!cannotRefresh}>
          {children}
        </PullToRefresh>
      </AntdContent>

      <AntdFooter className="mobile-layout__footer" variant={variant}>
        <BottomNavigation
          activeItem={getActiveItem()}
          onItemClick={handleBottomNavClick}
        />
      </AntdFooter>
    </AntdLayout>
  );
};

export default Layout;
